import { NmLoader } from '../../components/NmLoader';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../../components/NmButton';
import { comparisons as comparisonsApi } from '../../services/api';
import { COMPARISON_COLORS } from './types';

interface SharedComparisonReportProps {
  token: string;
  onClose: () => void;
}

interface SharedPayload {
  name?: string;
  tags?: string[];
  notes?: string;
  snapshot?: {
    workout_a_summary?: { title?: string; tss_actual?: number; total_time?: number };
    workout_b_summary?: { title?: string; tss_actual?: number; total_time?: number } | null;
    sparkline_a?: number[];
    sparkline_b?: number[];
    deltas?: Array<{ label: string; valueA: number | null; valueB: number | null; delta: number | null }>;
  };
  savedAt?: string;
}

function Sparkline({ pts, color }: { pts?: number[]; color: string }) {
  const w = 280;
  const h = 64;
  if (!pts?.length) return null;
  const max = Math.max(...pts, 1);
  const d = pts
    .map((v, i) => {
      const x = (i / Math.max(pts.length - 1, 1)) * w;
      const y = h - (v / max) * h;
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={d} fill="none" stroke={color} strokeWidth={2} />
    </svg>
  );
}

/** Read-only shared comparison view (snapshot only — no live streams). */
export function SharedComparisonReport({ token, onClose }: SharedComparisonReportProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<SharedPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void comparisonsApi
      .getShared(token)
      .then(res => {
        if (cancelled) return;
        setData((res.data as { data?: SharedPayload })?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setError(t('compare.shared.loadFailed'));
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{data?.name ?? t('compare.shared.title')}</h3>
          <NmButton variant="ghost" size="sm" onClick={onClose}>
            {t('analysis.close')}
          </NmButton>
        </div>
        {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
        {!error && !data && <p style={{ color: 'var(--fg-3)' }}><NmLoader size="sm" inline label={t('common.loading')} /></p>}
        {data && (
          <>
            {data.tags && data.tags.length > 0 && (
              <p style={{ fontSize: 12, color: 'var(--fg-3)' }}>{data.tags.join(' · ')}</p>
            )}
            {data.notes && <p style={{ fontSize: 13 }}>{data.notes}</p>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: COMPARISON_COLORS.a }}>
                  {data.snapshot?.workout_a_summary?.title ?? 'Workout A'}
                </div>
                <Sparkline pts={data.snapshot?.sparkline_a} color={COMPARISON_COLORS.a} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: COMPARISON_COLORS.b }}>
                  {data.snapshot?.workout_b_summary?.title ?? 'Workout B'}
                </div>
                <Sparkline pts={data.snapshot?.sparkline_b} color={COMPARISON_COLORS.b} />
              </div>
            </div>
            {data.savedAt && (
              <p style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 12 }}>
                Saved {new Date(data.savedAt).toLocaleString()}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(26,31,43,.5)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 9500,
};

const modal: React.CSSProperties = {
  width: 'min(640px, 94vw)',
  maxHeight: '85vh',
  overflow: 'auto',
  background: 'var(--surface-0)',
  borderRadius: 'var(--r-4)',
  padding: 20,
  boxShadow: 'var(--nm-raised-md)',
};
